<template>
  <div v-if="open" class="report-backdrop" @click.self="$emit('close')">
    <div class="report" role="dialog" aria-modal="true" aria-labelledby="report-title">
      <header class="report__head">
        <h2 id="report-title">Сообщить в IT</h2>
        <button class="report__close" type="button" aria-label="Закрыть" @click="$emit('close')">×</button>
      </header>

      <p class="report__lead">
        Опишите, что случилось. Мы сами добавим состояние системы, страницу и последние ошибки —
        IT разберётся без лишних вопросов.
      </p>

      <label class="report__label" for="report-trip">Номер рейса (если касается рейса)</label>
      <input id="report-trip" v-model="tripNumber" class="report__input" placeholder="например, 50754456" />

      <label class="report__label" for="report-comment">Что случилось</label>
      <textarea
        id="report-comment"
        v-model="comment"
        class="report__input report__textarea"
        rows="4"
        placeholder="Например: рейс не появился в ERP, хотя в TMS он есть с утра"
      ></textarea>

      <details class="report__preview">
        <summary>Что увидит IT</summary>
        <pre>{{ text }}</pre>
      </details>

      <footer class="report__foot">
        <span v-if="copied" class="report__ok">Скопировано. Вставьте в чат IT (Ctrl+V).</span>
        <button class="report__btn" type="button" @click="copy">Скопировать обращение</button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { buildItReport } from '@/pages/workspace/workspaceLogic.mjs'
import { recentErrors } from '@/backend/errorLog.mjs'

const props = defineProps({
  open: { type: Boolean, default: false },
  trip: { type: String, default: '' },
  health: { type: Object, default: null },
})
defineEmits(['close'])

const route = useRoute()
const store = useStore()
const comment = ref('')
const tripNumber = ref('')
const copied = ref(false)
const openedAt = ref('')

watch(() => props.open, (value) => {
  if (!value) return
  tripNumber.value = props.trip || ''
  copied.value = false
  openedAt.value = new Date().toLocaleString('ru-RU')
}, { immediate: true })

const userName = computed(() => {
  const u = store.state.user?.user
  if (!u) return ''
  const name = u.full_name || [u.last_name, u.first_name].filter(Boolean).join(' ') || u.name || u.username || u.email || ''
  return u.role?.name ? `${name} (${u.role.name})` : name
})

const text = computed(() => buildItReport({
  comment: comment.value,
  page: route.fullPath,
  user: userName.value,
  trip: tripNumber.value.trim(),
  health: props.health,
  errors: recentErrors(),
  now: openedAt.value,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
}))

async function copy() {
  try {
    await navigator.clipboard.writeText(text.value)
  } catch (e) {
    const area = document.createElement('textarea')
    area.value = text.value
    document.body.appendChild(area)
    area.select()
    document.execCommand('copy')
    document.body.removeChild(area)
  }
  copied.value = true
}
</script>

<style scoped>
.report-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.report {
  width: min(560px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 40px rgba(17, 24, 39, 0.25);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.report__head { display: flex; justify-content: space-between; align-items: center; }
.report__head h2 { font-size: 1.2rem; margin: 0; color: #111827; }
.report__close { border: 0; background: none; font-size: 1.6rem; line-height: 1; cursor: pointer; color: #6b7280; }
.report__lead { font-size: 0.875rem; color: #4b5563; line-height: 1.45; margin-bottom: 4px; }
.report__label { font-size: 0.8rem; font-weight: 600; color: #374151; margin-top: 4px; }
.report__input { border: 1px solid #d1d5db; border-radius: 8px; padding: 8px 12px; font: inherit; width: 100%; }
.report__input:focus { outline: 2px solid #c7d2fe; border-color: #4f46e5; }
.report__textarea { resize: vertical; }
.report__preview { font-size: 0.8rem; color: #4b5563; margin-top: 4px; }
.report__preview summary { cursor: pointer; color: #4f46e5; }
.report__preview pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  margin-top: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.report__foot { display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 8px; flex-wrap: wrap; }
.report__ok { font-size: 0.85rem; color: #15803d; }
.report__btn { border: 0; border-radius: 8px; padding: 9px 18px; background: #4f46e5; color: #fff; font: inherit; font-weight: 600; cursor: pointer; }
</style>
